import { THEME_SURFACE_TILE_KEY } from './surfaceTiles';

/**
 * Theme values that are NOT colours.
 *
 * The catalog (`THEME_VARS`) is colours and only colours: every entry is
 * hex-validated by the rebrand DTO, contrast-checked by `contrastChecks`, and
 * compared as parsed RGB by the theme migrations. An intensity enum in there
 * would be a foreign body in all three, so effects live here instead — stored
 * in the same layer records, cascaded by the same precedence, and applied
 * through a DOM ATTRIBUTE rather than a CSS variable.
 *
 * That last part is the whole design. `--mfw-glow-brand` is a token declared
 * once in `assets/scss/main.scss`; the handful of rules that light up name the
 * token; `:root[data-mfw-glow='off']` redefines it. So WHERE the app glows is
 * fixed by those rules, HOW MUCH is one attribute, and no component ever reads
 * a setting.
 *
 * Three settings live in a theme record today: `glow` (tenant default, org/corp
 * override), and the two org/corp-only ones, `surface` (the background tile,
 * `surfaceTiles.ts`) and `calendarColours` (below). `THEME_SETTING_KEYS` lists
 * all three, so the one place that must keep them out of the colour record —
 * the theme merge — has a single list to read.
 */

/**
 * The key an organization's (or tenant's) stored theme record uses.
 *
 * Deliberately NOT `--mfw-…`-prefixed: it is not a CSS variable and must never
 * be mistaken for one. `mergeOrgTheme` filters an org layer down to the
 * editable CATALOG VAR NAMES and strips the setting keys from the tenant layer
 * before merging, so this key cannot reach `:root` — the guarantee that makes it
 * safe to keep a non-colour in a colour record instead of adding a second
 * column, a second route and a migration.
 */
export const THEME_EFFECT_GLOW_KEY = 'glow';

/** How strongly the brand glow burns, or not at all. */
export const GLOW_INTENSITIES = ['off', 'subtle', 'full'] as const;

export type GlowIntensity = (typeof GLOW_INTENSITIES)[number];

/**
 * What the app glows at when nothing has an opinion.
 *
 * `full` rather than `off`: the effect is already rare by construction, and a
 * default of `off` would mean the tokens ship dead and the first person to look
 * concludes the feature never landed.
 */
export const DEFAULT_GLOW: GlowIntensity = 'full';

const isGlowIntensity = (value: unknown): value is GlowIntensity =>
  typeof value === 'string' &&
  (GLOW_INTENSITIES as readonly string[]).includes(value);

/**
 * The glow intensity in force, under the same precedence colours use:
 * catalog default ← tenant ← org.
 *
 * A stored row is data, not a promise, so an unrecognized value degrades to the
 * default instead of reaching the DOM — where it would land as an attribute no
 * stylesheet matches, i.e. silently `full`, which is the one outcome an admin
 * who set `off` must not get.
 */
export function resolveGlow(
  tenantTheme?: Record<string, unknown> | null,
  orgTheme?: Record<string, unknown> | null,
): GlowIntensity {
  for (const layer of [orgTheme, tenantTheme]) {
    const stored = layer?.[THEME_EFFECT_GLOW_KEY];
    if (isGlowIntensity(stored)) {
      return stored;
    }
  }
  return DEFAULT_GLOW;
}

/**
 * The key an org/corp theme record uses for its calendar colour mode.
 *
 * Same rules as `THEME_EFFECT_GLOW_KEY`: not `--mfw-`-prefixed, never a CSS
 * variable, an unknown stored value degrades to "absent". Org/corp only — the
 * tenant record does not carry it, so there is no tenant layer to cascade.
 */
export const THEME_CALENDAR_COLOURS_KEY = 'calendarColours';

/**
 * How the calendar tones its marks: from the org's brand, from neutral greys,
 * or the fixed status colours the calendar has always used.
 */
export const CALENDAR_COLOUR_MODES = ['brand', 'neutral', 'classic'] as const;

export type CalendarColourMode = (typeof CALENDAR_COLOUR_MODES)[number];

/**
 * What the calendar uses when the record says nothing — Brand, for every org
 * including the ones that never opened the editor (spec §6.1).
 */
export const DEFAULT_CALENDAR_COLOURS: CalendarColourMode = 'brand';

const isCalendarColourMode = (value: unknown): value is CalendarColourMode =>
  typeof value === 'string' &&
  (CALENDAR_COLOUR_MODES as readonly string[]).includes(value);

/**
 * The calendar colour mode in force for an org/corp record.
 *
 * Takes the RAW org/corp record and nothing else: unlike `resolveGlow` there is
 * no tenant layer to fall through to. A stored row is data, not a promise, so
 * `null`, a wrong type or an unknown/mis-cased string degrades to Brand rather
 * than reaching the DOM as an attribute no stylesheet matches.
 */
export function resolveCalendarColours(
  theme?: Record<string, unknown> | null,
): CalendarColourMode {
  const stored = theme?.[THEME_CALENDAR_COLOURS_KEY];
  return isCalendarColourMode(stored) ? stored : DEFAULT_CALENDAR_COLOURS;
}

/**
 * Every non-colour key a theme record may carry, whichever layer it sits on.
 *
 * None of them is a CSS variable, and none may reach the resolved palette (and
 * through it `:root`): the theme merge keeps them out of the colour record, and
 * reads each from the RAW layer record through its own resolver instead.
 */
export const THEME_SETTING_KEYS: readonly string[] = [
  THEME_EFFECT_GLOW_KEY,
  THEME_SURFACE_TILE_KEY,
  THEME_CALENDAR_COLOURS_KEY,
];
